<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import AddUserModal from './AddUserModal.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const accountId = computed(() => Number(route.params.superAdminAccountId));

const account = ref(null);
const showAddUserModal = ref(false);

const accountUsers = useMapGetter(
  'superAdminAccounts/getSuperAdminAccountUsers'
);
const uiFlags = useMapGetter('superAdminAccounts/getUIFlags');

const users = computed(() => accountUsers.value(accountId.value));

const fetchAccount = async () => {
  account.value = await store.dispatch(
    'superAdminAccounts/fetchAccount',
    accountId.value
  );
};

onMounted(fetchAccount);

const tableHeaders = computed(() => [
  t('SUPER_ADMIN.ACCOUNT_SHOW.TABLE.NAME'),
  t('SUPER_ADMIN.ACCOUNT_SHOW.TABLE.EMAIL'),
  t('SUPER_ADMIN.ACCOUNT_SHOW.TABLE.ROLE'),
  t('SUPER_ADMIN.ACCOUNT_SHOW.TABLE.STATUS'),
  t('SUPER_ADMIN.ACCOUNT_SHOW.TABLE.ACTIONS'),
]);

const toggleUserStatus = async accountUser => {
  const nextStatus = accountUser.status === 'active' ? 'suspended' : 'active';
  try {
    await store.dispatch('superAdminAccounts/updateAccountUser', {
      accountId: accountId.value,
      id: accountUser.id,
      status: nextStatus,
    });
    useAlert(t('SUPER_ADMIN.ACCOUNT_SHOW.STATUS_UPDATED'));
  } catch (error) {
    useAlert(
      error?.message || t('SUPER_ADMIN.ACCOUNT_SHOW.STATUS_UPDATE_ERROR')
    );
  }
};

const onUserAdded = () => {
  // Don't close the modal here — AddUserModal stays open on its own to show the generated
  // password (shown once, never again) when the user was new. It emits @close itself once the
  // SuperAdmin clicks "Done" there.
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingItem && !account"
    :loading-message="t('SUPER_ADMIN.ACCOUNT_SHOW.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="account ? account.name : ''"
        :description="t('SUPER_ADMIN.ACCOUNT_SHOW.DESCRIPTION')"
        :back-button-label="t('SUPER_ADMIN.ACCOUNT_SHOW.BACK')"
      >
        <template #actions>
          <a
            v-if="account"
            :href="`/super_admin/accounts/${account.id}/edit`"
            target="_blank"
            rel="noopener noreferrer"
            class="text-body-main text-n-blue-text hover:underline self-center ltr:mr-4 rtl:ml-4"
          >
            {{ t('SUPER_ADMIN.ACCOUNT_SHOW.EDIT_ADVANCED') }}
          </a>
          <Button
            :label="t('SUPER_ADMIN.ACCOUNT_SHOW.ADD_USER')"
            size="sm"
            @click="showAddUserModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="users"
        :no-data-message="t('SUPER_ADMIN.ACCOUNT_SHOW.NO_USERS')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="accountUser in items"
            :key="accountUser.id"
            :item="accountUser"
          >
            <template #default>
              <BaseTableCell>{{ accountUser.user.name }}</BaseTableCell>
              <BaseTableCell>{{ accountUser.user.email }}</BaseTableCell>
              <BaseTableCell>{{ accountUser.role }}</BaseTableCell>
              <BaseTableCell>
                <span
                  class="text-body-main"
                  :class="
                    accountUser.status === 'active'
                      ? 'text-n-teal-11'
                      : 'text-n-ruby-11'
                  "
                >
                  {{
                    t(
                      `SUPER_ADMIN.ACCOUNT_SHOW.STATUS_${accountUser.status.toUpperCase()}`
                    )
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <Button
                  :label="
                    accountUser.status === 'active'
                      ? t('SUPER_ADMIN.ACCOUNT_SHOW.SUSPEND')
                      : t('SUPER_ADMIN.ACCOUNT_SHOW.ACTIVATE')
                  "
                  slate
                  sm
                  :is-loading="uiFlags.updatingUser"
                  @click="toggleUserStatus(accountUser)"
                />
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal
      v-model:show="showAddUserModal"
      :on-close="() => (showAddUserModal = false)"
    >
      <AddUserModal
        :account-id="accountId"
        @close="showAddUserModal = false"
        @added="onUserAdded"
      />
    </woot-modal>
  </SettingsLayout>
</template>
